import type { ParentRollupQuery } from '@taler/api-client'
import { computed, type ComputedRef } from 'vue'
import { useRoute, useRouter, type LocationQueryRaw } from 'vue-router'
import {
  clampToToday,
  parseDateParam,
  parseOptionalDateParam,
  parsePeriodRollupBucketParam,
  parsePeriodRollupClassParam,
  type PERIOD_ROLLUP_BUCKETS,
  type PERIOD_ROLLUP_CLASSES,
} from '../internal/query-params.ts'

type Class = (typeof PERIOD_ROLLUP_CLASSES)[number]
type Bucket = (typeof PERIOD_ROLLUP_BUCKETS)[number]
type UrlQuery = Omit<ParentRollupQuery, 'class' | 'bucket' | 'displayCurrency'> & {
  class: Class
  bucket: Bucket
}

const QUERY_KEYS = ['class', 'bucket', 'from', 'until'] as const satisfies readonly (keyof UrlQuery)[]

const DEFAULT_CLASS: Class = 'EXPENSE'
const DEFAULT_BUCKET: Bucket = 'MONTH'

export type PeriodRollupFilterPatch = {
  readonly [K in Exclude<keyof ParentRollupQuery, 'displayCurrency'>]?: ParentRollupQuery[K] | undefined
}

export interface PeriodRollupQueryState {
  readonly query: ComputedRef<UrlQuery>
  setFilter(patch: PeriodRollupFilterPatch): void
}

/** `class`, `bucket`, `from` and `until` live in the URL, narrowed to the two classes and
 * two bucket sizes this screen offers (see `PERIOD_ROLLUP_CLASSES`/`PERIOD_ROLLUP_BUCKETS`).
 * The display currency is app-wide, not part of this view's query. No date may pass today. */
export function usePeriodRollupQueryState(today: () => string): PeriodRollupQueryState {
  const route = useRoute()
  const router = useRouter()

  const query = computed<UrlQuery>(() => {
    const raw = route.query as Record<string, unknown>
    const from = parseOptionalDateParam(raw['from'])
    return {
      class: parsePeriodRollupClassParam(raw['class'], DEFAULT_CLASS),
      bucket: parsePeriodRollupBucketParam(raw['bucket'], DEFAULT_BUCKET),
      until: clampToToday(parseDateParam(raw['until'], today()), today()),
      ...(from === undefined ? {} : { from }),
    }
  })

  function setFilter(patch: PeriodRollupFilterPatch): void {
    const merged: Partial<Record<keyof UrlQuery, ParentRollupQuery[keyof UrlQuery] | undefined>> = {
      ...query.value,
      ...patch,
    }
    if (merged.until !== undefined) merged.until = clampToToday(String(merged.until), today())
    if (merged.from !== undefined) merged.from = clampToToday(String(merged.from), today())
    const next: LocationQueryRaw = {}
    for (const key of QUERY_KEYS) {
      const value = merged[key]
      next[key] = value === undefined ? undefined : String(value)
    }
    void router.replace({ query: next })
  }

  return { query, setFilter }
}
